// ============================================================================
//   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
// ----------------------------------------------------------------------------
//   docTopics — comprehensive, task-oriented end-user documentation topics for
//   the Documentation hub. Each topic is authored markdown (served from
//   public/docs/, synced by scripts/copy-docs.mjs from repo docs/help/) and
//   rendered in-app via the same viewer as the guides (view-only, no download).
//   These REPLACE the old backend-driven category cards that routed to a
//   "content in preparation" stub.
// ============================================================================

/** A browsable documentation topic — a task-oriented guide to one product area. */
export interface DocTopic {
  /** URL slug for the in-app viewer route (/help/docs/guide/:slug). */
  slug: string;
  /** Human title shown on the topic card + viewer header. */
  title: string;
  /** One-line summary shown on the topic card. */
  description: string;
  /** ICON_MAP key (see DocumentationPage). */
  icon: string;
  /** Markdown filename served from public/docs/. */
  file: string;
  /** Extra search terms so the hub search matches on concepts, not just the title. */
  keywords: string;
  /** Topics are read in-app only — never downloadable. */
  downloadable?: false;
}

export const docTopics: DocTopic[] = [
  {
    slug: 'getting-started',
    title: 'Getting Started',
    description:
      'What Fabric Control is, how the app is organized, what your role can do, and where to begin.',
    icon: 'book-open',
    file: 'TOPIC_GETTING_STARTED.md',
    keywords:
      'overview intro onboarding navigation roles sidebar new user first steps welcome theme search preferences',
    downloadable: false,
  },
  {
    slug: 'dashboards-alerts',
    title: 'Dashboards & Alerts',
    description:
      'Reading your dashboard and working alerts — acknowledge to take ownership, resolve, and use the notification bell.',
    icon: 'bell',
    file: 'TOPIC_DASHBOARDS_ALERTS.md',
    keywords:
      'dashboard alerts alert bell notification acknowledge ownership resolve unattended owned severity kpi widget',
    downloadable: false,
  },
  {
    slug: 'capacity-management',
    title: 'Capacity Management',
    description:
      'Fabric capacities — utilization, throttling, right-sizing, dependencies, and cost.',
    icon: 'gauge',
    file: 'TOPIC_CAPACITY.md',
    keywords:
      'capacity sku f-sku compute utilization throttling right-sizing sla telemetry cost saturation dependencies',
    downloadable: false,
  },
  {
    slug: 'insights-analytics',
    title: 'Insights & Analytics',
    description:
      'Trend and analytics views — capacity, items, workspaces, storage, cost, growth forecasts, and metadata.',
    icon: 'trending-up',
    file: 'TOPIC_INSIGHTS.md',
    keywords:
      'insights analytics trends charts cost growth forecast storage workspace item warehouse azure infrastructure metadata lineage',
    downloadable: false,
  },
  {
    slug: 'resources-inventory',
    title: 'Resources & Inventory',
    description:
      'Browse and inspect everything Fabric Control manages — capacities, workspaces, lakehouses, warehouses, storage.',
    icon: 'boxes',
    file: 'TOPIC_RESOURCES.md',
    keywords:
      'resources inventory list catalog workspace lakehouse warehouse storage account filter search detail protection tab size',
    downloadable: false,
  },
  {
    slug: 'backup-recovery',
    title: 'Backup & Recovery',
    description:
      'Protect lakehouses and warehouses — plans, schedules, restore, recoverability, reports, and tiers.',
    icon: 'shield',
    file: 'TOPIC_BACKUP.md',
    keywords:
      'backup restore recovery plan schedule retention protection tier rpo rto recoverability recovery point reports lakehouse warehouse delta compare',
    downloadable: false,
  },
  {
    slug: 'data-collection',
    title: 'Data Collection',
    description:
      'Where the numbers come from — scheduled collection, freshness, sync, and reconciliation.',
    icon: 'refresh-cw',
    file: 'TOPIC_DATA_COLLECTION.md',
    keywords:
      'collection job tiers schedule freshness snapshot infrastructure sync discovery reconciliation stale not collected benchmarks',
    downloadable: false,
  },
  {
    slug: 'clients-sla',
    title: 'Clients & SLAs',
    description:
      'Manage client accounts and their SLA commitments — tiers, assignment, and blast-radius impact.',
    icon: 'building-2',
    file: 'TOPIC_CLIENTS_SLA.md',
    keywords:
      'client clients sla tier availability blast radius impact assignment configuration tenant',
    downloadable: false,
  },
  {
    slug: 'administration',
    title: 'Administration',
    description:
      'Run the platform — users and roles, system logs, alert rules, email notifications, and health.',
    icon: 'settings',
    file: 'TOPIC_ADMINISTRATION.md',
    keywords:
      'admin users roles permissions audit log authentication alert rules email notifications health platform infrastructure',
    downloadable: false,
  },
];
